import React, { useState, useEffect, useRef } from 'react';
import { ROUTES } from '../../helpers/contants';
import { Link } from 'react-router-dom';
import { RootState, useAppSelector } from '../../redux/store';

/**
 * Component that provides a floating menu with clickable items, toggled by a button.
 * @example
 * AddButton()
 * A JSX element with clickable menu items.
 * @param {boolean} isClicked - Represents the toggle state of the menu.
 * @returns {JSX.Element} A menu component with clickable links wrapped in a div.
 * @description
 *   - Utilizes `useState` and `useEffect` hooks to manage menu state and side-effects.
 *   - Handles outside clicks to close the menu when clicked outside.
 *   - Uses `useRef` to access the menu DOM element for click detection.
 */
const AddButton = () => {
  const [isClicked, setIsClicked] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { selectedEntity, permissionsData } = useAppSelector(
    (state: RootState) => state.common,
  );
  const canAccessStore =
    selectedEntity?.memberType === 'OWNER' ||
    !permissionsData?.[0]?.hideStore;

  const handleClick = () => {
    setIsClicked(prevState => !prevState);
  };

  const handleMenuItemClick = () => {
    setIsClicked(false); // Close the menu when an item is clicked
  };

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsClicked(false);
      }
    };

    if (isClicked) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isClicked]);

  return (
    <div ref={menuRef} className={`menu-floating ${isClicked ? 'active' : ''}`}>
      <ul className="menu-list">
        <li>
          <Link to={ROUTES.CREATE_STUDIO} onClick={handleMenuItemClick}>
            <i className="studio-new-icon"></i> Studio
          </Link>
        </li>
        <li>
          <Link to={ROUTES.CREATE_PRODUCTION} onClick={handleMenuItemClick}>
            <i className="production-icon"></i> Project
          </Link>
        </li>
        <li>
          <Link to={ROUTES.CREATE_EPISODE} onClick={handleMenuItemClick}>
            <i className="episode-icon"></i> Episode/Event
          </Link>
        </li>
        <li>
          <Link to={ROUTES.CREATE_SET} onClick={handleMenuItemClick}>
            <i className="set-icon"></i> Set/Activity
          </Link>
        </li>
        <li>
          <Link to={ROUTES.CREATE_LOCATION} onClick={handleMenuItemClick}>
            <i className="location-icon"></i> Location
          </Link>
        </li>

        {canAccessStore && (
          <li>
            <Link to={ROUTES.STORE} onClick={handleMenuItemClick}>
              <i className="order-icon"></i> Order
            </Link>
          </li>
        )}
      </ul>

      <button className="menu-btn" onClick={handleClick}>
        <i className="add-icon"></i>
      </button>
    </div>
  );
};

export default AddButton;
